window.HansoTheme = window.HansoTheme || {};

if (!customElements.get('custom-ajax-cart')) {
  class CustomAjaxCart extends HTMLElement {
    constructor() {
      super();

      this.activeElement = null;
      this.isBusy = false;
      this.quantityInputTimer = null;
      this.pendingOpen = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeyUp = this.handleKeyUp.bind(this);
    }

    connectedCallback() {
      // Cache the live DOM nodes every time the section mounts or re-renders.
      this.cacheDom();
      this.config = this.getConfig();
      this.sectionId = this.dataset.sectionId || this.config.sectionId || 'custom-ajax-cart-drawer';
      this.isPageMode = this.dataset.customCartPage === 'true';

      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('keyup', this.handleKeyUp);

      this.bindHeaderCartLinks();
      this.subscribeToCartUpdates();
      this.syncHeaderCartCount();
      this.refreshAffirmMessaging();

      window.HansoTheme.customAjaxCart = this;
      window.HansoTheme.customAjaxCartApi = {
        open: this.open.bind(this),
        close: this.close.bind(this),
        toggle: this.toggle.bind(this),
        refresh: this.refresh.bind(this),
      };
      window.openCustomAjaxCart = this.open.bind(this);
      window.closeCustomAjaxCart = this.close.bind(this);
      window.toggleCustomAjaxCart = this.toggle.bind(this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('keyup', this.handleKeyUp);

      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
    }

    cacheDom() {
      this.container = this.querySelector('[data-custom-cart-container]');
      this.drawer = this.querySelector('.cartDrawerArea');
      this.bodyArea = this.querySelector('[data-custom-cart-body]');
      this.footer = this.querySelector('[data-custom-cart-footer]');
    }

    getConfig() {
      const configScript = this.querySelector('[data-custom-cart-config]');

      if (!configScript) {
        return {};
      }

      try {
        return JSON.parse(configScript.textContent);
      } catch (error) {
        console.error('Failed to parse custom Ajax cart config.', error);
        return {};
      }
    }

    bindHeaderCartLinks() {
      const cartLinks = document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-mobile');

      cartLinks.forEach((link) => {
        if (link.dataset.customAjaxCartBound === 'true') {
          return;
        }

        link.dataset.customAjaxCartBound = 'true';
        link.setAttribute('role', 'button');
        link.setAttribute('aria-haspopup', 'dialog');

        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(link);
        });

        link.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() !== 'SPACE' && event.code.toUpperCase() !== 'ENTER') {
            return;
          }

          event.preventDefault();
          this.open(link);
        });
      });
    }

    subscribeToCartUpdates() {
      if (typeof subscribe !== 'function' || !window.PUB_SUB_EVENTS) {
        return;
      }

      this.cartUpdateUnsubscriber = subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'custom-ajax-cart') {
          return;
        }

        // Product forms already pass fresh section HTML into renderContents(),
        // so we skip an extra fetch when that payload is available.
        if (event.cartData?.sections?.[this.sectionId]) {
          return;
        }

        this.refresh({ keepOpen: this.isOpen() });
      });
    }

    isOpen() {
      return !!(this.container && this.container.classList.contains('showDrawer'));
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return [{ id: this.sectionId }];
    }

    open(triggeredBy) {
      if (triggeredBy) {
        this.setActiveElement(triggeredBy);
      }

      if (!this.container || !this.drawer) {
        this.pendingOpen = true;
        return;
      }

      this.pendingOpen = false;
      this.container.classList.add('showDrawer');
      document.body.classList.add('cartDrawerOpen');
      this.setAttribute('aria-hidden', 'false');
      if (!this.isPageMode) {
        window.HansoTheme.utils?.setScrollLock(true);
      }

      window.setTimeout(() => {
        if (!this.isPageMode && typeof trapFocus === 'function') {
          trapFocus(this.drawer, this.drawer.querySelector('.closeDrawerButton') || this.drawer);
        }
      }, 10);
    }

    close() {
      if (this.isPageMode) {
        return;
      }

      if (!this.container) {
        return;
      }

      this.pendingOpen = false;
      this.container.classList.remove('showDrawer');
      document.body.classList.remove('cartDrawerOpen');
      this.setAttribute('aria-hidden', 'true');
      window.HansoTheme.utils?.setScrollLock(false);

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement);
      }
    }

    toggle() {
      if (this.isOpen()) {
        this.close();
      } else {
        this.open();
      }
    }

    handleKeyUp(event) {
      if (event.code.toUpperCase() === 'ESCAPE') {
        this.close();
      }
    }

    handleClick(event) {
      const closeTrigger = event.target.closest('[data-close-cart-drawer]');
      if (closeTrigger) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeTrigger = event.target.closest('[data-custom-cart-remove]');
      if (removeTrigger) {
        event.preventDefault();
        if (this.isBusy) return;
        this.removeItem(removeTrigger.dataset.lineKey, removeTrigger.closest('[data-custom-cart-item]'));
        return;
      }

      const quantityTrigger = event.target.closest('[data-custom-cart-quantity-action]');
      if (quantityTrigger) {
        event.preventDefault();
        if (this.isBusy) return;

        const input = this.querySelector(
          `[data-custom-cart-quantity-input][data-line-key="${quantityTrigger.dataset.lineKey}"]`
        );

        if (!input) {
          return;
        }

        const action = quantityTrigger.dataset.customCartQuantityAction;
        const currentValue = parseInt(input.value, 10) || 1;
        const nextValue = action === 'decrease' ? Math.max(1, currentValue - 1) : currentValue + 1;

        input.value = nextValue;
        this.updateItemQuantity(quantityTrigger.dataset.lineKey, nextValue, input.closest('[data-custom-cart-item]'));
        return;
      }

      const shippingToggle = event.target.closest('[data-custom-cart-shipping-toggle]');
      if (shippingToggle) {
        event.preventDefault();
        if (this.isBusy) return;
        this.toggleShippingProtection(shippingToggle.closest('[data-custom-cart-shipping]'));
      }
    }

    handleChange(event) {
      const quantityInput = event.target.closest('[data-custom-cart-quantity-input]');
      if (!quantityInput || this.isBusy) {
        return;
      }

      const quantity = Math.max(1, parseInt(quantityInput.value, 10) || 1);
      quantityInput.value = quantity;

      if (this.quantityInputTimer) {
        window.clearTimeout(this.quantityInputTimer);
      }

      this.quantityInputTimer = window.setTimeout(() => {
        this.updateItemQuantity(quantityInput.dataset.lineKey, quantity, quantityInput.closest('[data-custom-cart-item]'));
      }, 120);
    }

    setBusyState(isBusy, scope) {
      this.isBusy = isBusy;
      this.classList.toggle('is-processing', isBusy);

      const interactiveElements = this.querySelectorAll(
        '.removeCartItemBtn, .qtyActionBtn, .cartQtyInput, [data-custom-cart-shipping-toggle], .checkoutBtn'
      );

      interactiveElements.forEach((element) => {
        if (isBusy) {
          if (!element.hasAttribute('data-original-disabled')) {
            element.setAttribute('data-original-disabled', element.disabled ? 'true' : 'false');
          }
          element.disabled = true;
        } else {
          const shouldStayDisabled = element.getAttribute('data-original-disabled') === 'true';
          element.disabled = shouldStayDisabled;
          element.removeAttribute('data-original-disabled');
        }
      });

      if (scope) {
        scope.classList.toggle('is-loading', isBusy);
      }
    }

    publishCartUpdate(cartData) {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'custom-ajax-cart',
          cartData: cartData,
        });
      }
    }

    async removeItem(lineKey, scope) {
      await this.changeCart({ id: lineKey, quantity: 0 }, scope);
    }

    async updateItemQuantity(lineKey, quantity, scope) {
      await this.changeCart({ id: lineKey, quantity: quantity }, scope);
    }

    async toggleShippingProtection(scope) {
      if (!scope) {
        return;
      }

      const lineKey = scope.dataset.shippingLineKey;
      const variantId = scope.dataset.shippingVariantId;

      if (lineKey) {
        await this.changeCart({ id: lineKey, quantity: 0 }, scope);
        return;
      }

      if (!variantId) {
        return;
      }

      await this.addItem({ id: Number(variantId), quantity: 1 }, scope);
    }

    async addItem(item, scope) {
      this.setBusyState(true, scope);

      try {
        const body = {
          items: [item],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };

        const response = await fetch(window.routes.cart_add_url, {
          ...fetchConfig('javascript'),
          body: JSON.stringify(body),
        });
        const parsedResponse = await response.json();

        if (parsedResponse.status) {
          throw new Error(parsedResponse.description || parsedResponse.message || 'Unable to add item to cart.');
        }

        this.renderContents(parsedResponse);
        this.publishCartUpdate(parsedResponse);
      } catch (error) {
        console.error(error);
      } finally {
        this.setBusyState(false, scope);
      }
    }

    async changeCart(payload, scope) {
      this.setBusyState(true, scope);

      try {
        const body = {
          ...payload,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        };

        const response = await fetch(window.routes.cart_change_url, {
          ...fetchConfig('javascript'),
          body: JSON.stringify(body),
        });
        const parsedResponse = await response.json();

        if (parsedResponse.errors) {
          throw new Error(parsedResponse.errors);
        }

        this.renderContents(parsedResponse);
        this.publishCartUpdate(parsedResponse);
      } catch (error) {
        console.error(error);
      } finally {
        this.setBusyState(false, scope);
      }
    }

    async refresh(options = {}) {
      try {
        const response = await fetch(`${window.routes.cart_url}?section_id=${encodeURIComponent(this.sectionId)}`);
        const html = await response.text();
        this.renderSectionMarkup(html, options);
      } catch (error) {
        console.error(error);
      }
    }

    renderContents(parsedState) {
      const sectionMarkup = parsedState.sections && parsedState.sections[this.sectionId];

      if (sectionMarkup) {
        this.renderSectionMarkup(sectionMarkup, { keepOpen: true });
        return;
      }

      this.refresh({ keepOpen: true });
    }

    renderSectionMarkup(sectionMarkup, options = {}) {
      // Preserve the current open state before replacing the server-rendered markup.
      const wasOpen = this.isOpen() || this.pendingOpen;
      const parsedDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
      const sourceCart = parsedDocument.querySelector('custom-ajax-cart');

      if (!sourceCart) {
        return;
      }

      this.innerHTML = sourceCart.innerHTML;
      this.className = sourceCart.className;
      this.dataset.cartCount = sourceCart.dataset.cartCount || '0';
      this.dataset.sectionId = sourceCart.dataset.sectionId || this.sectionId;

      this.cacheDom();
      this.config = this.getConfig();
      this.sectionId = this.dataset.sectionId || this.sectionId;
      this.isPageMode = this.dataset.customCartPage === 'true';
      this.syncHeaderCartCount();
      this.refreshAffirmMessaging();

      if (options.keepOpen || wasOpen) {
        this.open();
      } else {
        this.close();
      }
    }

    syncHeaderCartCount() {
      const count = parseInt(this.dataset.cartCount || '0', 10) || 0;
      const cartLinks = document.querySelectorAll('#cart-icon-bubble, #cart-icon-bubble-mobile');

      cartLinks.forEach((link) => {
        const defaultBadge = link.querySelector('.cart-count-bubble');
        const customBadge = link.querySelector('.hanso-header__cart-count');

        if (defaultBadge) {
          defaultBadge.remove();
        }

        if (customBadge) {
          customBadge.remove();
        }

        if (count <= 0) {
          return;
        }

        if (
          link.classList.contains('hanso-header__cart-link')
          || link.classList.contains('hanso-header__cart-link--mobile')
        ) {
          const badge = document.createElement('span');
          badge.className = 'hanso-header__cart-count';
          badge.textContent = count;
          link.appendChild(badge);
          return;
        }

        const bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';

        const number = document.createElement('span');
        number.setAttribute('aria-hidden', 'true');
        number.textContent = count;
        bubble.appendChild(number);

        const hiddenText = document.createElement('span');
        hiddenText.className = 'visually-hidden';
        hiddenText.textContent = count === 1 ? '1 item in cart' : `${count} items in cart`;
        bubble.appendChild(hiddenText);

        link.appendChild(bubble);
      });
    }

    refreshAffirmMessaging() {
      if (!window.affirm || !window.affirm.ui) {
        return;
      }

      window.affirm.ui.ready(() => {
        window.affirm.ui.refresh();
      });
    }
  }

  customElements.define('custom-ajax-cart', CustomAjaxCart);
}
